import 'react-loading-skeleton/dist/skeleton.css';
import 'assets/style/style.css';
import AuthGuard from 'components/auth/AuthGuard';
import GuestGuard from 'components/auth/GuestGuard';
import FallbackSpinner from 'components/spinner/FallbackSpinner';
import useToggleStyle from 'hooks/useToggleStyle';
import { useAppContext } from 'providers/AppProvider';
// import { useSettingsPanelContext } from 'providers/SettingsPanelProvider';
import { ReactNode, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from 'store';
import { Toaster } from 'react-hot-toast';
import { SkeletonTheme } from 'react-loading-skeleton';
import { ModalProvider } from 'providers/ConfirmationModalProvider';
import { AlertModalProvider } from 'providers/AlertModalProvider';
import PageLoadingProvider from 'providers/PageLoadingProvider';

const Guard = ({
  children,
  authGuard,
  guestGuard
}: {
  authGuard: boolean;
  guestGuard: boolean;
  children: ReactNode;
}) => {
  return (
    <>
      {guestGuard ? (
        <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>
      ) : !authGuard ? (
        <>{children}</>
      ) : (
        <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>
      )}
    </>
  );
};

const App = ({
  authGuard = true,
  guestGuard = false
}: {
  authGuard?: boolean;
  guestGuard?: boolean;
}) => {
  const { isStylesheetLoaded } = useToggleStyle();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Function to check session validity
  const checkSession = () => {
    const userDataString = window.localStorage.getItem('kerpUserData2.1');
    if (!userDataString) return false;

    const userData = JSON.parse(userDataString || '{}');
    const loginTime = new Date(userData.loginTime);
    const currentTime = new Date();
    const isInMobileApp: boolean = JSON.parse(
      window.localStorage.getItem('loginInMobileApp') || 'false'
    );

    if (
      currentTime.getTime() - loginTime.getTime() < 1 * 30 * 60 * 1000 ||
      isInMobileApp
    ) {
      // Update login time to extend session
      userData.loginTime = currentTime;
      window.localStorage.setItem('kerpUserData2.1', JSON.stringify(userData));
      return true;
    } else {
      // Session expired, clear data
      window.localStorage.setItem('kerpUserData2.1', '');
      window.localStorage.setItem('authConfig.storageTokenKeyName', 'null');
      window.localStorage.setItem('krpLogin', 'false');
      return false;
    }
  };

  useEffect(() => {
    // Check session on component mount
    const isSessionValid = checkSession();

    if (
      !isSessionValid &&
      !pathname.includes('/auth/login') &&
      !pathname.includes('/auth/autoLogin')
    ) {
      navigate('/auth/login', {
        state: { returnUrl: pathname },
        replace: true
      });
    }

    // Set up interval to check session periodically
    const sessionCheckInterval = setInterval(
      () => {
        const isStillValid = checkSession();
        if (
          !isStillValid &&
          !pathname.includes('/auth/login') &&
          !pathname.includes('/auth/autoLogin')
        ) {
          navigate('/auth/login', {
            state: { returnUrl: pathname },
            replace: true
          });
        }
      },
      5 * 60 * 1000
    ); // Check every 5 minutes

    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, [pathname, navigate]);
  // const { setSettingsPanelConfig } = useSettingsPanelContext();
  const {
    config: { theme }
  } = useAppContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // useEffect(() => {
  //   setSettingsPanelConfig({ openSettingPanel: false });
  // }, [isRTL]);

  return (
    <>
      {!isStylesheetLoaded && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: theme === 'dark' ? '#000' : '#fff'
          }}
        />
      )}
      {isStylesheetLoaded && (
        <Provider store={store}>
          <Guard authGuard={authGuard} guestGuard={guestGuard}>
            {/* // baseColor={theme === 'dark' ? '#0F111A' : '#ebebeb'}
            // highlightColor={theme === 'dark' ? '#1D2332' : '#f5f5f5'} */}
            <SkeletonTheme
              baseColor={theme === 'dark' ? '#1D2332' : '#e3e6ed'}
              highlightColor={theme === 'dark' ? '#0F111A' : '#f5f7fa'}
            >
              <PageLoadingProvider>
                <AlertModalProvider>
                  <ModalProvider>
                    <Outlet />
                  </ModalProvider>
                </AlertModalProvider>
              </PageLoadingProvider>
            </SkeletonTheme>
          </Guard>
          <Toaster position="top-center" />
        </Provider>
      )}
    </>
  );
};

export default App;
